import { Box, Paper, Stack, useTheme } from "@carrotfertility/carotene-core"
import React from "react"
import { perViewport } from "./responsive"

// Derived from Box rather than importing the (unexported) SxProps type.
type BoxSx = React.ComponentProps<typeof Box>["sx"]

/**
 * The Appointments page's loading state: skeletons mirroring the fullest common frame (Up next card,
 * Past list, booking card) so the layout doesn't jump. No design frame exists for this state yet.
 */

const ROW_AVATAR_SIZE = perViewport(48, 54)

/**
 * One pulsing placeholder block. `aria-hidden`: the announcement is on the container's `role=status`.
 * Exported so `HomeAppointmentsTile`'s skeleton pulses identically inside its own shell.
 */
export function SkeletonBlock({
  width,
  height,
  circular = false,
  sx
}: {
  width?: number | string
  height?: number | string
  circular?: boolean
  sx?: BoxSx
}): JSX.Element {
  const theme = useTheme()
  return (
    <Box
      aria-hidden
      // Array form is MUI's supported sx merge (a caller sx may be a function/array); caller wins, last.
      sx={[
        {
          inlineSize: width,
          blockSize: height,
          flexShrink: 0,
          backgroundColor: theme.palette.background.hover,
          borderRadius: circular ? "50%" : theme.tokens.borderRadius.sm,
          "@keyframes carrotApptSkeletonPulse": {
            "0%": { opacity: 1 },
            "50%": { opacity: 0.4 },
            "100%": { opacity: 1 }
          },
          animation: "carrotApptSkeletonPulse 1.6s ease-in-out infinite",
          "@media (prefers-reduced-motion: reduce)": { animation: "none" }
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : [])
      ]}
    />
  )
}

/** A provider/visit row: avatar + two lines of text, the shape both Up next and Past rows share. */
function SkeletonRow(): JSX.Element {
  const theme = useTheme()
  return (
    <Stack sx={{ flexDirection: "row", alignItems: "center" }} spacing={theme.spacing(theme.tokens.spacing.md)}>
      <SkeletonBlock circular sx={{ inlineSize: ROW_AVATAR_SIZE, blockSize: ROW_AVATAR_SIZE }} />
      <Stack sx={{ flexDirection: "column" }} spacing={theme.spacing(theme.tokens.spacing.xs)}>
        <SkeletonBlock width={168} height={16} />
        <SkeletonBlock width={216} height={14} />
      </Stack>
    </Stack>
  )
}

function SkeletonCard({ children }: { children: React.ReactNode }): JSX.Element {
  const theme = useTheme()
  return (
    <Paper
      elevation={0}
      sx={{
        padding: theme.spacing(theme.tokens.spacing.lg),
        borderRadius: theme.tokens.borderRadius.md
      }}
    >
      {children}
    </Paper>
  )
}

export function AppointmentsLoading(): JSX.Element {
  const theme = useTheme()
  return (
    <Stack
      role="status"
      aria-label="Loading your appointments"
      sx={{ flexDirection: "column" }}
      spacing={theme.spacing(theme.tokens.spacing.xl)}
    >
      {/* Up next */}
      <Stack sx={{ flexDirection: "column" }} spacing={theme.spacing(theme.tokens.spacing.md)}>
        <SkeletonBlock width={96} height={24} />
        <SkeletonCard>
          <SkeletonRow />
          <SkeletonBlock
            width={128}
            height={44}
            sx={{
              marginBlockStart: theme.spacing(theme.tokens.spacing.lg),
              borderRadius: theme.tokens.borderRadius.md
            }}
          />
        </SkeletonCard>
      </Stack>

      {/* Past */}
      <Stack sx={{ flexDirection: "column" }} spacing={theme.spacing(theme.tokens.spacing.md)}>
        <SkeletonBlock width={64} height={20} />
        <SkeletonRow />
        <SkeletonRow />
      </Stack>

      {/* Book appointment */}
      <SkeletonCard>
        <Stack sx={{ flexDirection: "row" }} spacing={theme.spacing(theme.tokens.spacing.xs)}>
          <SkeletonBlock circular width={40} height={40} />
          <SkeletonBlock circular width={40} height={40} />
          <SkeletonBlock circular width={40} height={40} />
        </Stack>
        <SkeletonBlock width={220} height={18} sx={{ marginBlockStart: theme.spacing(theme.tokens.spacing.md) }} />
        <SkeletonBlock width="70%" height={14} sx={{ marginBlockStart: theme.spacing(theme.tokens.spacing.sm) }} />
        <SkeletonBlock
          width={160}
          height={44}
          sx={{
            marginBlockStart: theme.spacing(theme.tokens.spacing.lg),
            borderRadius: theme.tokens.borderRadius.md
          }}
        />
      </SkeletonCard>
    </Stack>
  )
}
